import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useJsonLd, getSiteUrl } from "@/hooks/useJsonLd";

export default function ArticleOpinie() {
  const navigate = useNavigate();
  const siteUrl = getSiteUrl();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Opinie Użytkowników Kakobuy – Prawdziwe Recenzje | Kakobuy Spreadsheet"; }, []);

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Opinie Użytkowników Kakobuy Spreadsheet – Prawdziwe Recenzje Polaków",
    "description": "Autentyczne recenzje polskich użytkowników — sprawdź, co mówią o Kakobuy Spreadsheet i czy warto z niego korzystać.",
    "url": `${siteUrl}/artykul/opinie-uzytkownikow-kakobuy`,
    "datePublished": "2026-06-21",
    "dateModified": "2026-06-21",
    "inLanguage": "pl",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "image": "https://readdy.ai/api/search-image?query=Happy%20person%20unboxing%20package%20on%20light%20peach%20background%2C%20warm%20natural%20lighting%2C%20editorial%20lifestyle%20photography%2C%20authentic%20joyful%20expression%2C%20modern%20European%20aesthetic%2C%20soft%20shadows&width=1600&height=900&seq=kako-art-05-hero&orientation=landscape",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artykul/opinie-uzytkownikow-kakobuy`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://readdy.ai/api/search-image?query=Happy%20person%20unboxing%20package%20on%20light%20peach%20background%2C%20warm%20natural%20lighting%2C%20editorial%20lifestyle%20photography%2C%20authentic%20joyful%20expression%2C%20modern%20European%20aesthetic%2C%20soft%20shadows&width=1600&height=900&seq=kako-art-05-hero&orientation=landscape)" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="relative z-10 flex-1 flex items-center pt-24 pb-12">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <span className="inline-block px-4 py-1.5 bg-background-50/60 backdrop-blur-md rounded-full text-xs font-semibold text-foreground-800 mb-6 whitespace-nowrap">Blog &gt; Opinie</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">Opinie Użytkowników<br />Kakobuy Spreadsheet</h1>
              <p className="text-base text-background-50/85 mt-4 max-w-2xl">Autentyczne recenzje polskich użytkowników — sprawdź, co mówią o Kakobuy Spreadsheet i czy warto z niego korzystać.</p>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Co Polacy mówią o <span className="italic text-foreground-600 font-normal">Kakobuy Spreadsheet</span>?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  {[{ name: "Kamil, Warszawa", text: "Zamówiłem buty przez arkusz Kakobuy i jestem zachwycony. Jakość przewyższa oczekiwania, a cena była o połowę niższa niż w Polsce. Polecam każdemu!", rating: 5 }, { name: "Natalia, Kraków", text: "Korzystam z arkusza od 3 miesięcy. Bluzy i koszulki, które zamówiłam są świetnej jakości. Dostawa trwała 14 dni — warto było czekać.", rating: 5 }, { name: "Michał, Gdańsk", text: "Początkowo bałem się zamawiać z Chin, ale Kakobuy Spreadsheet rozwiał moje obawy. Wszystkie linki są sprawdzone, a produkty dokładnie takie jak na zdjęciach.", rating: 5 }, { name: "Dominika, Wrocław", text: "Super sprawa! Zamówiłam akcesoria i perfumy — wszystko dotarło w nienaruszonym stanie. Arkusz jest bardzo intuicyjny i oszczędza mnóstwo czasu.", rating: 4 }].map((r, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-1 mb-2">{[...Array(r.rating)].map((_, j) => <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>)}</div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">{r.text}</p>
                      <p className="text-xs font-semibold text-foreground-900">{r.name}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Podsumowanie opinii</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">Zdecydowana większość opinii o <strong>Kakobuy Spreadsheet</strong> jest pozytywna. Użytkownicy chwalą przede wszystkim jakość produktów w stosunku do ceny, intuicyjność arkusza oraz szybkość dostawy do Polski. Negatywne opinie są rzadkie i najczęściej dotyczą wydłużonego czasu dostawy w okresach świątecznych.</p>
              </section>

              <section className="text-center py-10">
                <p className="text-lg text-foreground-700 mb-6">Dołącz do zadowolonych użytkowników <strong>Kakobuy Spreadsheet</strong>.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="bg-foreground-900 text-background-50 px-7 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">Sprawdź Kakobuy Spreadsheet</a>
                  <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}><i className="ri-arrow-left-line mr-1"></i>Strona główna</a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}